import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import "./Result.css";

// Helper function to map card string to image path (same as ResultABJ)
const getCardImage = (cardCode) => {
  if (!cardCode) return null;
  try {
    if (cardCode.length < 2) {
      return null;
    }
    const cleanCode = cardCode.replace(/\.(jpg|png)$/i, '');
    return `/assets/img/cards/${cleanCode}.png`;
  } catch (e) {
    return null;
  }
};

// Helper function to parse card string and split into Dragon and Tiger cards
const parseCards = (cardString) => {
  if (!cardString) return { dragon: null, tiger: null };
  
  const cards = cardString.split(',');
  const dragonCardCode = cards[0] ? cards[0].trim() : null;
  const tigerCardCode = cards[1] ? cards[1].trim() : null;
  
  return { 
    dragon: dragonCardCode ? { code: dragonCardCode, image: getCardImage(dragonCardCode) } : null,
    tiger: tigerCardCode ? { code: tigerCardCode, image: getCardImage(tigerCardCode) } : null
  };
};

// Helper function to parse rdesc: "Tiger#No#D : Odd  |  T : Even#D : Red  |  T : Black#D : 6  |  T : 4"
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};
  
  const parts = rdesc.split('#');
  const winner = parts[0] || "";
  const pair = parts[1] || "No";
  const oddEvenPart = parts[2] || "";
  const colorPart = parts[3] || "";
  const cardPart = parts[4] || "";
  
  return {
    winner,
    pair,
    oddEven: oddEvenPart,
    color: colorPart,
    card: cardPart,
  };
};

const ResultDragonTiger202 = ({ 
  data = [], 
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('dt202');
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            const mappedResults = response.data.data.res.map(item => ({
              mid: item.mid,
              win: item.win === "1" ? "D" : item.win === "2" ? "T" : item.win
            }));
            setLastResults(mappedResults);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            const mappedResults = response.data.res.map(item => ({
              mid: item.mid,
              win: item.win === "1" ? "D" : item.win === "2" ? "T" : item.win
            }));
            setLastResults(mappedResults);
          }
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);
    
    const mid = resultItem?.mid;
    
    if (mid) {
      try {
        const response = await getDetailResults('dt202', mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
    setShowModal(true);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    const cards = detailResult?.card ? parseCards(detailResult.card) : { dragon: null, tiger: null };
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winner = detailResult?.winnat || rdescData.winner || "";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      dragon: cards.dragon,
      tiger: cards.tiger,
      resultDetails: {
        winner: winner,
        pair: rdescData.pair || "No",
        oddEven: rdescData.oddEven || "",
        color: rdescData.color || "",
        card: rdescData.card || "",
      },
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0 
    ? lastResults
    : data.map((res, idx) => ({ win: res === "D" ? "D" : res === "T" ? "T" : res, mid: null }));

  return (
    <>
      <div className="result-row dragon-tiger casino-last-results">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          const isDragon = win === "D" || win === "1";
          return (
            <div
              key={`${resultItem.mid || index}-${index}`}
              className={`result-circle ${isDragon ? "result-dragon" : "result-tiger"}`}
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ 
                cursor: resultItem.mid ? 'pointer' : 'default',
                color: isDragon ? '#ff4500' : '#ff3'
              }}
            >
              {isDragon ? "D" : "T"}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="20-20 Dragon Tiger 2 Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="row bottom-border-casino m-t-10">
            <div className="col-md-12">
              <div className="player-image-container row">
                <div className="text-center player-number col-12">
                  {modalData.dragon?.image && (
                    <img src={modalData.dragon.image} alt="Dragon Card" onError={(e) => e.target.style.display = 'none'} />
                  )}
                  {modalData.tiger?.image && (
                    <img src={modalData.tiger.image} alt="Tiger Card" onError={(e) => e.target.style.display = 'none'} />
                  )}
                  <div className="col-md-12 text-left">
                    {modalData.resultDetails.winner && (
                      <p className="m-b-0 m-t-20">
                        <b>Winner:</b> {modalData.resultDetails.winner}
                      </p>
                    )}
                    <p className="m-b-0">
                      <b>Pair:</b> {modalData.resultDetails.pair || 'No'}
                    </p>
                    {modalData.resultDetails.oddEven && (
                      <p className="m-b-0">
                        <b>Odd/Even:</b> {modalData.resultDetails.oddEven}
                      </p>
                    )}
                    {modalData.resultDetails.color && (
                      <p className="m-b-0">
                        <b>Color:</b> {modalData.resultDetails.color}
                      </p>
                    )}
                    {modalData.resultDetails.card && (
                      <p className="m-b-0">
                        <b>Card:</b> {modalData.resultDetails.card}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultDragonTiger202;

